import { Children, cloneElement, isValidElement, type ReactNode } from 'react';
import { Button, Spinner, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { SPACE_X } from '../styles/scale';
import { useSurfaceStyles } from '../styles/surfaces';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';

/**
 * The one action bar (#202).
 *
 * `RequestForm` hand-assembles four near-identical wizard footers — three
 * different flex containers, three different button orders, and none of them
 * sticky, so on a long form the primary action scrolls out of reach. The
 * reviewer's decision bar has the opposite problem: it shares
 * `colorNeutralBackground1` with every card on the page and carries no shadow,
 * so once a subsection scrolls under it, it reads as part of that card rather
 * than as a bar above it. Both are the same missing component.
 *
 * ── EXACTLY ONE PRIMARY BUTTON ──
 * The row can render at most one `appearance="primary"`, and only from the
 * `primary` slot — that is why `primary` is a `{ label, onClick }` object
 * rather than a `ReactNode`: the emphasised action is not something a caller
 * composes, it is something the bar owns. `secondary` takes arbitrary nodes and
 * **must never** contain a button with `appearance="primary"`; two competing
 * filled buttons is precisely the defect this component exists to stop.
 *
 * ── DESTRUCTIVE IS NOT BRAND ──
 * `appearance="primary"` on a destroy action is banned. With a saturated indigo
 * primary, the loudest, most inviting thing on a "Remove this person?" dialog
 * would be the button that removes them. So `destructive` routes emphasis AWAY
 * from the brand: the confirm becomes a `secondary` button wearing
 * `colorPaletteRedForeground1`/`colorPaletteRedBorder2`, and its cancel sibling
 * (the `back` slot) drops to `subtle`. The row still reads correctly with no
 * colour at all — high contrast discards the palette the same way it discards
 * the brand hue — because the label ("Remove", "Reject request") carries the
 * meaning and the cancel is a full-weight button beside a bare one.
 *
 * ── ONE BUSY FLAG ──
 * `busy` is a single prop for the whole row, not per button. A half-disabled
 * action bar (Save spinning, Close still live) is how a requester leaves a form
 * mid-write; every control in the row reflects the same in-flight state, and
 * the arbitrary `secondary` nodes are cloned with `disabled` so they cannot
 * opt out of it.
 *
 * ── WIREFRAME NOTES ──
 * D1: the form footer's two non-primary actions are **"Close"** and
 * **"Save & close"** — never "Cancel". (R2 says "Cancel"; D1 is the
 * authoritative sheet for that button and `RequestForm` already follows it.)
 * K1: a sticky decision bar must be **last in DOM order**, so a keyboard user
 * reaches Approve only after reading the request. `sticky` is therefore CSS
 * (`position: sticky`) and nothing else — do not portal this component, and do
 * not move it up the tree to get it to float.
 */
export interface ActionBarProps {
  /**
   * The leading, lowest-emphasis action: "Close", "← Back to details", or the
   * cancel of a destructive confirm. Rendered first in DOM order and pushed to
   * the leading edge; absent, the whole row sits at the trailing edge.
   */
  back?: { label: string; onClick: () => void };
  /**
   * Extra trailing actions ("Save & close", "Send back") — anything but a
   * primary-appearance button. Cloned with `disabled` while `busy`.
   */
  secondary?: ReactNode;
  /** The single emphasised action. The only `appearance="primary"` in the row. */
  primary?: { label: string; onClick: () => void; disabled?: boolean };
  /** Confirms a destroy/reject. Moves emphasis off the brand — see the docblock. */
  destructive?: boolean;
  /** One in-flight flag for the entire row: disables every button, spins the primary. */
  busy?: boolean;
  /** Floats the bar above content scrolling under it (E2 — `useSurfaceStyles().stickyBar`). */
  sticky?: boolean;
}

const useStyles = makeStyles({
  /**
   * `space-between` puts `back` at the leading edge and the trailing cluster at
   * the far edge. Below the breakpoint the row becomes a column so no action is
   * ever clipped or shrunk below its touch target — Teams forbids sideways
   * scroll, and a wrapped half-button is the same defect with extra steps.
   */
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE_X.s,
    flexWrap: 'wrap',
    '@media (max-width: 700px)': { flexDirection: 'column', alignItems: 'stretch' },
  },

  /** No `back`: the cluster is the only child, so it must be told to sit trailing. */
  trailingOnly: { justifyContent: 'flex-end' },

  /**
   * The trailing cluster. `SPACE_X.s` is the "buttons within one action
   * cluster" step — deliberately tighter than the gap to `back`, which is a
   * different kind of action and reads as a separate group.
   */
  trailing: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
    flexWrap: 'wrap',
    '@media (max-width: 700px)': { flexDirection: 'column', alignItems: 'stretch' },
  },

  /**
   * The destructive confirm. Fluent's `Button` sets its own foreground, so
   * unlike a hand-rolled `<button>` (CLAUDE.md entry 54) this only has to
   * override it — and it has to override the hover/pressed states too, or
   * Fluent's own `:hover` rule pulls the colour back to neutral mid-gesture.
   */
  destructive: {
    color: tokens.colorPaletteRedForeground1,
    // `border`, NOT `borderColor`: Griffel rejects `borderColor` as an
    // unsupported CSS shorthand (it warns to stderr and DROPS the rule), so a
    // red-bordered button written that way is silently a grey-bordered one.
    // The width and style restate what Fluent's own secondary button already
    // uses, so only the colour actually changes.
    border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    ':hover': {
      color: tokens.colorPaletteRedForeground1,
      border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    },
    ':hover:active': {
      color: tokens.colorPaletteRedForeground1,
      border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    },
  },
});

/**
 * Pushes the row's single `busy` flag into caller-supplied nodes. A `ReactNode`
 * cannot be disabled from the outside any other way, and leaving these live
 * while the primary spins is exactly the half-disabled row the flag exists to
 * prevent.
 */
function withBusy(node: ReactNode, busy: boolean): ReactNode {
  if (!busy) return node;
  return Children.map(node, (child) =>
    isValidElement<{ disabled?: boolean }>(child) ? cloneElement(child, { disabled: true }) : child,
  );
}

export function ActionBar({
  back,
  secondary,
  primary,
  destructive = false,
  busy = false,
  sticky = false,
}: ActionBarProps) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();

  return (
    <div
      className={mergeClasses(
        styles.row,
        back === undefined && styles.trailingOnly,
        sticky && surfaces.stickyBar,
        responsive.touchTargets,
      )}
      // Announced only while it means something; a permanent `aria-busy="false"`
      // is noise on a bar that is idle almost all of the time.
      aria-busy={busy || undefined}
    >
      {back !== undefined && (
        <Button
          appearance={destructive ? 'subtle' : 'secondary'}
          disabled={busy}
          onClick={back.onClick}
        >
          {back.label}
        </Button>
      )}
      <div className={styles.trailing}>
        {withBusy(secondary, busy)}
        {primary !== undefined && (
          <Button
            appearance={destructive ? 'secondary' : 'primary'}
            className={destructive ? styles.destructive : undefined}
            disabled={busy || primary.disabled === true}
            icon={busy ? <Spinner size="tiny" /> : undefined}
            onClick={primary.onClick}
          >
            {primary.label}
          </Button>
        )}
      </div>
    </div>
  );
}
